import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Link2, RefreshCw, CheckCircle, AlertCircle, Settings,
  Video, Mail, Send, Cloud, Loader2, Calendar,
  FileText, CheckSquare, Clock, ArrowRight, Inbox
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const Integrations = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [integrations, setIntegrations] = useState(null);
  const [syncing, setSyncing] = useState({
    teams: false,
    email: false
  });
  const [teamsMeetings, setTeamsMeetings] = useState([]);
  const [recentEmails, setRecentEmails] = useState([]);
  const [processedEmails, setProcessedEmails] = useState([]);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/meeting-intelligence/integrations/status?token=${token}`);
      setIntegrations(res.data.integrations);
    } catch (err) {
      console.error('Error fetching integrations:', err);
      toast.error('Failed to load integration status');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const syncTeamsMeetings = async () => {
    try {
      setSyncing(prev => ({ ...prev, teams: true }));
      const res = await axios.post(`${API}/api/meeting-intelligence/integrations/teams/sync?token=${token}`);
      
      toast.success(`Synced ${res.data.synced} meetings from Teams${res.data.mode === 'mock' ? ' (Mock mode)' : ''}`);
      
      // Refresh Teams meetings list
      await fetchTeamsMeetings();
    } catch (err) {
      console.error('Error syncing Teams:', err);
      toast.error('Failed to sync Teams meetings');
    } finally {
      setSyncing(prev => ({ ...prev, teams: false }));
    }
  };

  const fetchTeamsMeetings = async () => {
    try {
      const res = await axios.get(`${API}/api/meeting-intelligence/integrations/teams/meetings?token=${token}`);
      setTeamsMeetings(res.data.meetings || []);
    } catch (err) {
      console.error('Error fetching Teams meetings:', err);
    }
  };

  const processEmails = async () => {
    try {
      setSyncing(prev => ({ ...prev, email: true }));
      const res = await axios.post(`${API}/api/meeting-intelligence/integrations/email/process?token=${token}`);
      
      const tasksCount = res.data.tasks_created?.length || 0;
      toast.success(`Processed ${res.data.emails_processed} emails, created ${tasksCount} tasks${res.data.mode === 'mock' ? ' (Mock mode)' : ''}`);
      
      // Refresh email lists
      await fetchRecentEmails();
      await fetchProcessedEmails();
    } catch (err) {
      console.error('Error processing emails:', err);
      toast.error('Failed to process emails');
    } finally {
      setSyncing(prev => ({ ...prev, email: false }));
    }
  };

  const fetchRecentEmails = async () => {
    try {
      const res = await axios.get(`${API}/api/meeting-intelligence/integrations/email/recent?token=${token}&hours=24`);
      setRecentEmails(res.data.emails || []);
    } catch (err) {
      console.error('Error fetching recent emails:', err);
    }
  };

  const fetchProcessedEmails = async () => {
    try {
      const res = await axios.get(`${API}/api/meeting-intelligence/integrations/email/processed?token=${token}&limit=20`);
      setProcessedEmails(res.data.processed_emails || []);
    } catch (err) {
      console.error('Error fetching processed emails:', err);
    }
  };

  useEffect(() => {
    if (!loading && integrations) {
      fetchTeamsMeetings();
      fetchRecentEmails();
      fetchProcessedEmails();
    }
  }, [loading, integrations]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="meeting-integrations">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <Link2 className="h-7 w-7 text-blue-600" />
            Integrations
          </h1>
          <p className="text-slate-500 mt-1">Connect external services to Meeting Intelligence</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchStatus}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh Status
        </Button>
      </div>

      {/* Integration Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Microsoft Teams */}
        <Card className={`border-l-4 ${integrations?.microsoft_teams?.configured ? 'border-l-emerald-500' : 'border-l-amber-500'}`}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <Video className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold">Microsoft Teams</h3>
                  <p className="text-sm text-slate-500">{integrations?.microsoft_teams?.description}</p>
                </div>
              </div>
              <Badge variant={integrations?.microsoft_teams?.configured ? 'default' : 'secondary'}>
                {integrations?.microsoft_teams?.mode}
              </Badge>
            </div>
            <div className="mt-4">
              {!integrations?.microsoft_teams?.configured && (
                <p className="text-xs text-amber-600 mb-2">
                  Add Azure AD credentials to enable live sync
                </p>
              )}
              <Button 
                size="sm" 
                className="w-full"
                onClick={syncTeamsMeetings}
                disabled={syncing.teams}
              >
                {syncing.teams ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Sync Meetings
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Email Ingestion */}
        <Card className={`border-l-4 ${integrations?.email_ingestion?.configured ? 'border-l-emerald-500' : 'border-l-amber-500'}`}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Inbox className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold">Email Ingestion</h3>
                  <p className="text-sm text-slate-500">{integrations?.email_ingestion?.description}</p>
                </div>
              </div>
              <Badge variant={integrations?.email_ingestion?.configured ? 'default' : 'secondary'}>
                {integrations?.email_ingestion?.mode}
              </Badge>
            </div>
            <div className="mt-4">
              {!integrations?.email_ingestion?.configured && (
                <p className="text-xs text-amber-600 mb-2">
                  Configure email provider for live ingestion
                </p>
              )}
              <Button 
                size="sm" 
                className="w-full"
                onClick={processEmails}
                disabled={syncing.email}
              >
                {syncing.email ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Mail className="h-4 w-4 mr-2" />
                )}
                Process Emails
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Email Sending */}
        <Card className={`border-l-4 ${integrations?.email_sending?.configured ? 'border-l-emerald-500' : 'border-l-amber-500'}`}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <Send className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold">Email Sending</h3>
                  <p className="text-sm text-slate-500">{integrations?.email_sending?.description}</p>
                </div>
              </div>
              <Badge variant={integrations?.email_sending?.configured ? 'default' : 'secondary'}>
                {integrations?.email_sending?.mode}
              </Badge>
            </div>
            <div className="mt-4">
              {!integrations?.email_sending?.configured && (
                <p className="text-xs text-amber-600 mb-2">
                  Add SendGrid/SES keys to enable real emails
                </p>
              )}
              <Button size="sm" variant="outline" className="w-full" disabled>
                <Settings className="h-4 w-4 mr-2" />
                Configure Provider
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Details */}
      <Tabs defaultValue="teams" className="space-y-4">
        <TabsList>
          <TabsTrigger value="teams" className="flex items-center gap-2">
            <Video className="h-4 w-4" />
            Teams Meetings
          </TabsTrigger>
          <TabsTrigger value="emails" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Email Inbox
          </TabsTrigger>
          <TabsTrigger value="processed" className="flex items-center gap-2">
            <CheckSquare className="h-4 w-4" />
            Processed
          </TabsTrigger>
        </TabsList>

        {/* Teams Meetings Tab */}
        <TabsContent value="teams">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5 text-purple-600" />
                Teams Calendar
              </CardTitle>
              <CardDescription>
                {integrations?.microsoft_teams?.mode === 'mock' 
                  ? 'Showing mock data - add Azure credentials for real meetings'
                  : 'Meetings from your Microsoft Teams calendar'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {teamsMeetings.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Video className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No Teams meetings found</p>
                  <Button size="sm" variant="outline" className="mt-4" onClick={syncTeamsMeetings}>
                    Sync Now
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {teamsMeetings.map((meeting, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                          <Video className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium">{meeting.subject}</p>
                          <p className="text-sm text-slate-500">
                            {meeting.start ? new Date(meeting.start).toLocaleString() : 'No date'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {meeting.has_transcript && (
                          <Badge variant="outline" className="text-emerald-600">
                            <FileText className="h-3 w-3 mr-1" />
                            Transcript
                          </Badge>
                        )}
                        {meeting.join_url && (
                          <Button size="sm" variant="ghost" asChild>
                            <a href={meeting.join_url} target="_blank" rel="noopener noreferrer">
                              Join <ArrowRight className="h-3 w-3 ml-1" />
                            </a>
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Emails Tab */}
        <TabsContent value="emails">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Inbox className="h-5 w-5 text-blue-600" />
                Recent Emails
              </CardTitle>
              <CardDescription>
                {integrations?.email_ingestion?.mode === 'mock' 
                  ? 'Showing mock data - configure email provider for real inbox'
                  : 'Emails from the last 24 hours'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {recentEmails.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Mail className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No recent emails</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentEmails.map((email, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium">{email.subject}</p>
                          <p className="text-sm text-slate-500">From: {email.from_name || email.from_email}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {email.has_attachments && (
                            <Badge variant="outline" className="text-blue-600">
                              Attachments
                            </Badge>
                          )}
                          <span className="text-xs text-slate-400">
                            <Clock className="h-3 w-3 inline mr-1" />
                            {new Date(email.received_at).toLocaleString()}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-slate-600 mt-2 line-clamp-2">
                        {email.body?.substring(0, 200)}...
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Processed Emails Tab */}
        <TabsContent value="processed">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CheckSquare className="h-5 w-5 text-emerald-600" />
                Processed Emails
              </CardTitle>
              <CardDescription>Emails that have been analyzed and processed</CardDescription>
            </CardHeader>
            <CardContent>
              {processedEmails.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No processed emails yet</p>
                  <Button size="sm" variant="outline" className="mt-4" onClick={processEmails}>
                    Process Now
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {processedEmails.map((email, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="h-5 w-5 text-emerald-500" />
                        <div>
                          <p className="font-medium">{email.subject}</p>
                          <p className="text-sm text-slate-500">From: {email.from_email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        {email.result?.tasks?.length > 0 && (
                          <Badge className="bg-blue-100 text-blue-700">
                            {email.result.tasks.length} tasks created
                          </Badge>
                        )}
                        <p className="text-xs text-slate-400 mt-1">
                          {new Date(email.processed_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Integrations;
